import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import logoImage from "@/assets/logo-power.png";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useAdminRole } from "@/hooks/useAdminRole";

interface NavItem {
  to: string;
  label: string;
  isAnchor?: boolean;
  isAction?: boolean;
  action?: () => void;
}

export default function MobileNav() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: displayName } = useProfile();
  const { isAdmin } = useAdminRole();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const userLabel = displayName || user?.email?.split("@")[0] || "Account";

  const navItems: NavItem[] = [
    { to: location.pathname === "/" ? "#calculator" : "/#calculator", label: t("nav.calculator"), isAnchor: location.pathname === "/" },
    { to: "/standards", label: t("nav.standards") },
    { to: "/community", label: t("nav.community") },
    { to: "/workout-log", label: t("nav.workoutLog") },
    ...(isAdmin ? [{ to: "/admin", label: "Admin" }] : []),
    user
      ? { to: "#", label: t("nav.signOut", "Sign Out"), isAction: true, action: handleSignOut }
      : { to: "/auth", label: t("nav.signIn") },
  ];

  return (
    <header className="border-b border-border/50 sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex items-center justify-between h-14 lg:h-16">
        <Link to="/" className="flex items-center gap-2">
          <img src={logoImage} alt="Strength Calculator logo" className="w-11 h-11 lg:w-13 lg:h-13 rounded-md bg-black" />
          <span className="font-heading text-sm lg:text-xl font-bold tracking-wide">{t("brandName")}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 text-sm text-muted-foreground">
          {navItems.map((item) =>
            item.isAnchor ? (
              <a key={item.label} href={item.to} className="hover:text-foreground transition-colors">
                {item.label}
              </a>
            ) : item.isAction ? (
              <div key={item.label} className="flex items-center gap-2">
                <Link to="/profile" className="text-sm text-primary max-w-[120px] truncate hover:underline -ml-4">
                  {userLabel}
                </Link>
                <button
                  onClick={item.action}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              </div>
            ) : (
              <Link key={item.to} to={item.to} className={`hover:text-foreground transition-colors${item.to === "/workout-log" ? " -ml-2" : ""}`}>
                {item.label}
              </Link>
            )
          )}
          <LanguageSwitcher />
        </nav>

        {/* Mobile hamburger */}
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 bg-card border-border p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-border/50">
                  <span className="font-heading text-lg font-bold">{t("brandName")}</span>
                </div>
                {/* User info in mobile menu */}
                {user && (
                  <div className="px-4 py-3 border-b border-border/50 bg-secondary/30">
                    <p className="text-xs text-muted-foreground">Signed in as</p>
                    <Link to="/profile" onClick={() => setOpen(false)} className="text-sm font-medium text-primary truncate hover:underline">
                      {userLabel}
                    </Link>
                  </div>
                )}
                <nav className="flex flex-col p-4 gap-1">
                  {navItems.map((item) =>
                    item.isAnchor ? (
                      <a
                        key={item.label}
                        href={item.to}
                        onClick={() => setOpen(false)}
                        className="px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : item.isAction ? (
                      <button
                        key={item.label}
                        onClick={() => { setOpen(false); item.action?.(); }}
                        className="px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors text-left flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        className="px-3 py-2.5 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
